import React from 'react';
import {
  Box, SvgIconProps, Typography, useTheme,
} from '@mui/material';
import { green } from '@mui/material/colors';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { PARTNERS_ROUTE } from '../routes/namedRoutes';
import { BadgeType } from './enums';
import CallToActionButton from './CallToActionButton';
import { addUserInitiatedHomeDepotEnrollmentParam } from './homeDepotEnrollmentIntent';

interface BadgeProps {
  badgeType: BadgeType;
  badgeText: string;
  badgeSubtitle?: string | null;
  isButton?: boolean;
  redirectPath?: string;
  optionalStyles?: React.CSSProperties;
  updateOpenEnrollmentModalState?: ((event: boolean) => void);
  onNavigate?: () => void;
  leadingIcon?: React.ReactNode | React.ElementType<SvgIconProps>;
  size?: 'default' | 'small';
}

interface CheckedBadgeProps {
  badgeText: string;
  leadingIcon?: React.ReactNode | React.ElementType<SvgIconProps>;
}

interface EnrolledBadgeProps {
  badgeText: string;
  badgeType: BadgeType;
  badgeSubtitle?: string | null;
}

interface NotEnrolledBadgeProps {
  badgeText: string;
  badgeSubtitle?: string | null;
}

const CheckedBadge = ({ badgeText, leadingIcon }: CheckedBadgeProps) => {
  const theme = useTheme();

  return (
    <>
      {React.isValidElement(leadingIcon) ? leadingIcon : null}
      <Typography
        variant="h10"
        sx={{
          paddingRight: theme.spacing(0.25),
          [theme.breakpoints.down('sm')]: {
            fontSize: theme.spacing(1.5),
          },
        }}
      >
        {badgeText}
      </Typography>
    </>
  );
};

const EnrolledBadge = ({ badgeText, badgeType }: EnrolledBadgeProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          lineHeight: theme.spacing(3),
          [theme.breakpoints.down('sm')]: {
            fontSize: theme.spacing(2),
          },
        }}
      >
        {badgeText}
      </Typography>
      {badgeType === BadgeType.ManageRewards && <ArrowForwardIcon
        sx={{
          width: theme.spacing(2.5),
          height: theme.spacing(2.5),
          ml: theme.spacing(0.75),
        }}
      />}
    </Box>
  );
};

const NotEnrolledBadge = ({
  badgeText, badgeSubtitle,
}: NotEnrolledBadgeProps) => {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <AddIcon
          sx={{
            width: theme.spacing(2.5),
            height: theme.spacing(2.5),
            mr: theme.spacing(0.75),
          }}
        />
        <Typography
          variant="h6"
          sx={{
            lineHeight: theme.spacing(3),
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.spacing(2),
            },
          }}
        >
          {badgeText}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="h13"
          sx={{
            lineHeight: theme.spacing(2),
          }}
        >
          {badgeSubtitle}
        </Typography>
      </Box>
    </>
  );
};

export function Badge({
  badgeType,
  badgeText,
  badgeSubtitle,
  isButton = false,
  optionalStyles = {},
  updateOpenEnrollmentModalState,
  redirectPath,
  onNavigate,
  leadingIcon,
  size = 'default',
}: BadgeProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isCheckBadge = badgeType === BadgeType.Checkmark;

  const sizeStyles = size === 'small' ? {
    fontSize: theme.spacing(1.5),
    height: theme.spacing(2.5),
    lineHeight: theme.spacing(2),
    padding: `${theme.spacing(0.25)} ${theme.spacing(0.875)}`,
    width: 'fit-content',
  } : {
    padding: `${theme.spacing(0.25)} ${theme.spacing(1.25)}`,
    width: '100%',
  };

  const redirectToPath = () => {
    if (redirectPath) {
      const redirectPathQueryString = redirectPath.includes('?') ? redirectPath.split('?')[1] : '';
      const queryParams = new URLSearchParams(redirectPathQueryString);
      const returnPath = queryParams.get('returnPath') || PARTNERS_ROUTE;
      const redirectWithParam = addUserInitiatedHomeDepotEnrollmentParam(redirectPath);

      if (onNavigate) onNavigate();
      navigate(redirectWithParam, {
        state: {
          userInitiatedHdEnrollment: true,
          returnPath,
        },
      });
    } else {
      navigate(PARTNERS_ROUTE);
    }
  };

  const handleClickNotEnrolled = () => {
    if (updateOpenEnrollmentModalState) updateOpenEnrollmentModalState(true);
    else redirectToPath();
  };

  const RenderBadgeContent = () => {
    switch (badgeType) {
      case BadgeType.Checkmark:
        return (
          <CheckedBadge
            badgeText={badgeText}
          />
        );
      case BadgeType.Enrolled:
      case BadgeType.ManageRewards:
        return (
          <EnrolledBadge
            badgeText={badgeText}
            badgeSubtitle={badgeSubtitle}
            badgeType={badgeType}
          />
        );
      case BadgeType.NotEnrolled:
        return (
          <NotEnrolledBadge
            badgeText={badgeText}
            badgeSubtitle={badgeSubtitle}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isCheckBadge ? <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: optionalStyles.backgroundColor || green[50],
          color: optionalStyles.color || green[800],
          border: `${theme.spacing(0.125)} solid ${optionalStyles.borderColor || green[100]}`,
          borderRadius: theme.spacing(12.5),
          gap: theme.spacing(0.2),
          textAlign: 'left',
          whiteSpace: 'nowrap',
          ...sizeStyles,
          ...optionalStyles,
        }}
      >
        <CheckedBadge
          badgeText={badgeText}
          leadingIcon={leadingIcon}
        />
      </Box> :
        <CallToActionButton
          data-testid="partners-badge-btn"
          text={<RenderBadgeContent />}
          type={'button'}
          onBtnClick={handleClickNotEnrolled}
          sx={{
            position: 'static',
            width: '100%',
            height: 'fit-content',
            marginBottom: theme.spacing(0),
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            fontWeight: '600',
            fontSize: theme.spacing(1.75),
            background: optionalStyles.backgroundColor || theme.palette.brand.primary,
            border: `1px solid ${optionalStyles.borderColor || theme.palette.brand.primary}`,
            color: optionalStyles.color || theme.palette.common.white,
            borderRadius: theme.spacing(1),
            '&:hover': {
              backgroundColor: (optionalStyles.backgroundColor ||
                theme.palette.brand.primary),

            },
            '&:disabled': {
              backgroundColor: theme.palette.common.white,
              color: theme.palette.grey[400],
              opacity: '1',
              border: `1px solid ${theme.palette.grey[200]}`,
              boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
            },
            [theme.breakpoints.up('sm')]: {
              width: '100%',
              marginLeft: '0px',
            },
          }}
          disable={badgeType === BadgeType.Enrolled}
        />}
    </>
  );
}
